import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { useContent } from "../lib/content";
import { Quote, Send, CheckCircle2 } from "lucide-react";

export default function Reviews() {
  const kicker = useContent("page.reviews.kicker", "/ 12 — REVIEWS");
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);
  const title = useContent("page.reviews.title", "WHAT PEOPLE SAY");
  const intro = useContent(
    "page.reviews.intro",
    "Words from promoters, fellow artists and dancers who experienced Inner Pulse live."
  );
  const emptyMsg = useContent(
    "page.reviews.empty_state",
    "Be the first to share a review. Approved testimonials will appear here."
  );
  const formTitle = useContent("page.reviews.form_title", "Write a review");
  const privacyNote = useContent(
    "page.reviews.privacy_note",
    "Your email will stay private — only your name and message will be displayed after approval."
  );
  const successMsg = useContent(
    "page.reviews.success_msg",
    "Your review has been received and will appear here once approved."
  );

  useEffect(() => {
    api
      .get("/reviews")
      .then((r) => setReviews(r.data))
      .finally(() => setLoading(false));
  }, []);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("All fields are required");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/reviews", form);
      setSent(true);
      setForm({ name: "", email: "", message: "" });
      toast.success("Thank you. Your review is pending approval.");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="reviews-page" className="px-6 md:px-12 py-20">
      <div className="max-w-[1400px] mx-auto">
        <div className="mb-16" data-testid="reviews-header">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-8 h-px bg-silver-400" />
            <span className="font-title text-[0.65rem] tracking-[0.4em] text-silver-400">
              {kicker}
            </span>
          </div>
          <h1 className="font-title text-silver-glow text-4xl sm:text-5xl lg:text-7xl leading-[0.95] tracking-[0.06em] whitespace-pre-line">
            {title}
          </h1>
          <p className="font-body text-silver-400 text-sm md:text-base max-w-2xl mt-8 leading-relaxed">
            {intro}
          </p>
        </div>

        {/* REVIEWS LIST */}
        <section className="mb-20" data-testid="reviews-list-section">
          {loading && (
            <p className="font-body text-silver-500 text-sm">Loading…</p>
          )}
          {!loading && reviews.length === 0 && (
            <div className="hairline-t pt-16 flex flex-col items-center gap-4 text-center" data-testid="reviews-empty">
              <Quote size={32} className="text-silver-700" />
              <p className="font-body text-silver-500 text-sm max-w-md">
                {emptyMsg}
              </p>
            </div>
          )}
          {!loading && reviews.length > 0 && (
            <div className="grid md:grid-cols-2 gap-px bg-silver-800">
              {reviews.map((r) => (
                <article
                  key={r.id}
                  className="bg-black p-8 flex flex-col gap-4"
                  data-testid={`review-${r.id}`}
                >
                  <Quote size={20} className="text-silver-500" />
                  <p className="font-body text-silver-200 text-base leading-relaxed">
                    "{r.message}"
                  </p>
                  <div className="hairline-t pt-4 mt-auto">
                    <div className="font-title text-sm tracking-[0.1em] text-silver-100">
                      {r.name}
                    </div>
                    <div className="font-title text-[0.58rem] tracking-[0.3em] text-silver-500 mt-1">
                      {new Date(r.created_at).toLocaleDateString("en-GB", {
                        month: "long",
                        year: "numeric",
                      })}
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </section>

        {/* FORM */}
        <section className="hairline-t pt-12" data-testid="reviews-form-section">
          <div className="grid md:grid-cols-12 gap-10">
            <div className="md:col-span-4">
              <div className="font-title text-[0.62rem] tracking-[0.3em] text-silver-600 mb-4">
                / SHARE YOUR EXPERIENCE
              </div>
              <h2 className="font-title text-2xl md:text-3xl tracking-[0.1em] text-silver-100 mb-4">
                {formTitle}
              </h2>
              <p className="font-body text-silver-500 text-xs leading-relaxed">
                {privacyNote}
              </p>
            </div>

            <div className="md:col-span-8">
              {sent ? (
                <div className="flex flex-col items-start gap-4" data-testid="review-success">
                  <CheckCircle2 size={28} className="text-silver-100" />
                  <h3 className="font-title text-2xl text-silver-100 tracking-[0.1em]">
                    Thank you.
                  </h3>
                  <p className="font-body text-silver-400 text-sm max-w-md">
                    {successMsg}
                  </p>
                  <button
                    className="btn-ghost"
                    onClick={() => setSent(false)}
                    data-testid="review-write-another"
                  >
                    WRITE ANOTHER
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} className="flex flex-col gap-6" data-testid="review-form">
                  <div className="grid md:grid-cols-2 gap-x-10 gap-y-6">
                    <label>
                      <span className="ip-label">Your Name *</span>
                      <input
                        className="ip-input"
                        value={form.name}
                        onChange={update("name")}
                        required
                        data-testid="review-name"
                      />
                    </label>
                    <label>
                      <span className="ip-label">Email (kept private) *</span>
                      <input
                        type="email"
                        className="ip-input"
                        value={form.email}
                        onChange={update("email")}
                        required
                        data-testid="review-email"
                      />
                    </label>
                  </div>
                  <label>
                    <span className="ip-label">Your Review *</span>
                    <textarea
                      className="ip-input resize-none"
                      rows={5}
                      value={form.message}
                      onChange={update("message")}
                      required
                      data-testid="review-message"
                    />
                  </label>
                  <div className="flex items-center justify-between flex-wrap gap-4 pt-2">
                    <p className="font-body text-silver-600 text-xs">
                      Reviews are moderated before they appear publicly.
                    </p>
                    <button
                      type="submit"
                      className="btn-sharp"
                      disabled={submitting}
                      data-testid="review-submit"
                    >
                      <Send size={12} /> &nbsp; {submitting ? "Sending…" : "Submit Review"}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </section>
      </div>
    </div>
  );
}
